import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, MapPin, User, Truck, FileText, Building2, Info } from 'lucide-react';
import { toast } from 'sonner';

// 2026-07-05 Wave 2 #4: Read-only detail page for DEDICATED_SITE work
// schedules. Keeps DedSite entirely off the SR-style detail page.
export default function DedicatedSiteScheduleDetailPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/work-schedules/${id}`);
        if (res.data?.schedule_type !== 'DEDICATED_SITE') {
          // Wrong page for this record — bounce to the SR detail page.
          navigate(`/work-schedules/${id}`, { replace: true });
          return;
        }
        setWs(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load schedule');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, api, navigate]);

  if (loading) return <div className="p-6 text-[#5A5A5A]">Loading…</div>;
  if (!ws) return null;

  const ded = ws.dedicated_site || {};
  const jobLead = ws.job_lead;
  const supportTeam = ws.support_team || [];
  const vehicles = ws.vehicles || [];

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto" data-testid="dedicated-site-schedule-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="ds-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#323033] flex items-center gap-2">
            {ded.repair_site_name || 'Dedicated Site'}
            <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-200">Dedicated Site</Badge>
            <Badge className="bg-slate-100 text-slate-700 border">{ws.status}</Badge>
          </h1>
          <p className="text-sm text-[#5A5A5A]">View-only Dedicated Site schedule</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/work-schedules/${ws.id}/edit`)} data-testid="ds-edit-btn" hidden={user?.role === 'TECHNICIAN'} className={user?.role === 'TECHNICIAN' ? 'hidden' : ''}>
          Edit
        </Button>
      </div>

      {/* Schedule window + facility */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-[#005EF8]" /> Schedule</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[#808080] text-xs">Start Date</div>
            <div className="font-medium">{ws.schedule_start_date || '—'}</div>
          </div>
          <div>
            <div className="text-[#808080] text-xs">End Date</div>
            <div className="font-medium">{ws.schedule_end_date || '—'}</div>
          </div>
          <div>
            <div className="text-[#808080] text-xs">Facility</div>
            <div className="font-medium flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-[#5A5A5A]" /> {ws.facility?.name || '—'}</div>
          </div>
        </CardContent>
      </Card>

      {/* Dedicated Site details */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-[#005EF8]" /> Site Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><div className="text-[#808080] text-xs">Site Name</div><div className="font-medium">{ded.repair_site_name || '—'}</div></div>
          <div><div className="text-[#808080] text-xs">SPLC Code</div><div className="font-medium">{ded.splc_code || '—'}</div></div>
          <div className="md:col-span-2"><div className="text-[#808080] text-xs">Address</div><div className="font-medium">{ded.address || '—'}</div></div>
          {ded.address_line_2 && <div className="md:col-span-2"><div className="text-[#808080] text-xs">Address Line 2</div><div className="font-medium">{ded.address_line_2}</div></div>}
          <div><div className="text-[#808080] text-xs">City</div><div className="font-medium">{ded.city || '—'}</div></div>
          <div><div className="text-[#808080] text-xs">State</div><div className="font-medium">{ded.state_province || '—'}</div></div>
          <div><div className="text-[#808080] text-xs">ZIP Code</div><div className="font-medium">{ded.zip_code || '—'}</div></div>
          <div><div className="text-[#808080] text-xs">Site Contact</div><div className="font-medium">{ded.site_contact || '—'}</div></div>
          <div><div className="text-[#808080] text-xs">Site Contact Phone</div><div className="font-medium">{ded.site_contact_number || '—'}</div></div>
          <div><div className="text-[#808080] text-xs">Site Contact Email</div><div className="font-medium">{ded.site_contact_email || '—'}</div></div>
        </CardContent>
      </Card>

      {/* Entry Requirements */}
      {(ded.entry_requirements || '').trim() && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Info className="w-4 h-4 text-[#005EF8]" /> Entry Requirements</CardTitle></CardHeader>
          <CardContent className="text-sm whitespace-pre-wrap">{ded.entry_requirements}</CardContent>
        </Card>
      )}

      {/* Crew */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4 text-[#005EF8]" /> Crew</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div><span className="text-[#808080] text-xs">Job Lead:</span> <span className="font-medium">{jobLead ? `${jobLead.first_name || ''} ${jobLead.last_name || ''}`.trim() : '—'}</span></div>
          <div>
            <span className="text-[#808080] text-xs">Support Team:</span>{' '}
            <span className="font-medium">
              {supportTeam.length ? supportTeam.map(m => `${m.first_name || ''} ${m.last_name || ''}`.trim()).join(', ') : '—'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Vehicles */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Truck className="w-4 h-4 text-[#005EF8]" /> Vehicles</CardTitle></CardHeader>
        <CardContent className="text-sm">
          {ws.vehicle_not_needed
            ? <span className="text-[#5A5A5A] italic">Vehicle not needed</span>
            : (vehicles.length
                ? <ul className="list-disc pl-5">{vehicles.map(v => <li key={v.id}>{v.name || v.vin || v.id}</li>)}</ul>
                : <span className="text-[#5A5A5A]">—</span>)}
        </CardContent>
      </Card>

      {/* Notes */}
      {(ws.notes || '').trim() && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-[#005EF8]" /> Notes</CardTitle></CardHeader>
          <CardContent className="text-sm whitespace-pre-wrap">{ws.notes}</CardContent>
        </Card>
      )}
    </div>
  );
}
